import { Info, Link2, MoreHorizontal, Search, Unlink } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

import { casePath } from "../../caseIde/routes";
import { companyActions } from "../actions";
import { capUsed, caseFeeText } from "../billingModel";
import { CaseRequestQueue } from "../components/CaseRequestQueue";
import { LinkCasesDialog } from "../components/LinkCasesDialog";
import type { Company, CompanyCaseRow, MatterIds } from "../types";
import { filterCases, formatDeadline, isSoon, type CaseFilter } from "../utils";

const COLS =
  "grid grid-cols-[minmax(0,1.9fr)_minmax(0,0.7fr)_minmax(0,1fr)_120px_104px_124px_minmax(0,0.9fr)_32px] items-center gap-3 px-4";
const ALL = "__all__";
const NONE = "__none__";

/** Saves on blur or Enter when the value changed. */
const InlineId: FC<{
  value: string | null;
  label: string;
  onSave: (value: string) => void;
}> = ({ value, label, onSave }) => {
  const [text, setText] = useState(value ?? "");
  useEffect(() => setText(value ?? ""), [value]);
  const commit = () => {
    if (text.trim() !== (value ?? "")) onSave(text.trim());
  };
  return (
    <Input
      aria-label={label}
      value={text}
      placeholder="–"
      className="h-7 border-transparent bg-transparent px-1.5 font-mono text-sm shadow-none hover:border-line-2 focus-visible:border-line-2"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setText(e.currentTarget.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setText(value ?? "");
          e.currentTarget.blur();
        }
      }}
    />
  );
};

const FeeCell: FC<{ fee: CompanyCaseRow["fee"] }> = ({ fee }) => {
  if (!fee) return <span className="text-sm text-fg-3">–</span>;
  const used = capUsed(fee);
  return (
    <span className="flex min-w-0 flex-col gap-1" data-testid="case-fee">
      <span
        className={cn(
          "truncate text-sm tabular-nums",
          used !== null && used >= 90 && "font-medium text-warn"
        )}
        title={
          fee.company_terms
            ? "From the company's fee schedule"
            : "Set on this case"
        }
      >
        {caseFeeText(fee)}
        {fee.employer_share < 100 ? (
          <span className="text-fg-3">
            {" "}
            · company pays {fee.employer_share}%
          </span>
        ) : null}
      </span>
      {used !== null ? (
        <Progress
          value={used}
          aria-label="Share of the not-to-exceed amount used"
          className={cn("h-1", used >= 90 && "[&>*]:bg-warn")}
        />
      ) : null}
    </span>
  );
};

type Props = {
  company: Company;
  cases: CompanyCaseRow[] | undefined;
  loading: boolean;
};

export const EmployeesTab: FC<Props> = ({ company, cases, loading }) => {
  const navigate = useNavigate();
  const openCase = (r: CompanyCaseRow) =>
    navigate(
      casePath({
        individualId: r.individual_id,
        foreignNationalId: r.foreign_national_id
      })
    );
  const [filter, setFilter] = useState<CaseFilter>({
    query: "",
    show: "all",
    entityId: null
  });
  const [linkOpen, setLinkOpen] = useState(false);
  const rows = filterCases(cases ?? [], filter);

  const saveId =
    (row: CompanyCaseRow, field: keyof MatterIds) => (value: string) =>
      void companyActions.saveMatterId(
        company,
        row.individual_id,
        field,
        value,
        row[field]
      );

  const setPart = (
    row: CompanyCaseRow,
    key: "entity_id" | "job_description_id",
    value: string | null
  ) =>
    void companyActions.updateCase(
      company,
      row.individual_id,
      { [key]: value },
      { [key]: row[key] }
    );

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4"
      data-testid="company-employees"
    >
      <CaseRequestQueue company={company} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-60">
          <Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-fg-3" />
          <Input
            aria-label="Search employees"
            placeholder="Search employees"
            className="h-8 pl-8 text-base"
            value={filter.query}
            onChange={(e) => {
              const query = e.currentTarget.value;
              setFilter((f) => ({ ...f, query }));
            }}
          />
        </div>
        {company.entities.length > 1 ? (
          <Select
            value={filter.entityId ?? ALL}
            onValueChange={(v) =>
              setFilter((f) => ({ ...f, entityId: v === ALL ? null : v }))
            }
          >
            <SelectTrigger
              size="sm"
              aria-label="Entity"
              className="h-8 w-48 text-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Every Entity</SelectItem>
              {company.entities.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.legal_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <span className="flex-1" />
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={filter.show}
          onValueChange={(v) =>
            v && setFilter((f) => ({ ...f, show: v as CaseFilter["show"] }))
          }
        >
          <ToggleGroupItem value="open" className="px-2.5 text-sm">
            Open
          </ToggleGroupItem>
          <ToggleGroupItem value="all" className="px-2.5 text-sm">
            All
          </ToggleGroupItem>
        </ToggleGroup>
        <Button size="sm" variant="secondary" onClick={() => setLinkOpen(true)}>
          <Link2 /> Link cases
        </Button>
      </div>

      {loading ? (
        <Skeleton className="h-40" />
      ) : (
        <div
          className="shrink-0 overflow-hidden rounded-lg border border-line-2"
          role="table"
        >
          <div
            className={cn(
              COLS,
              "h-9 border-b border-line-2 bg-fill-1 text-sm font-medium text-fg-3"
            )}
            role="row"
          >
            <span>Employee</span>
            <span>Case</span>
            <span>Job</span>
            <span>Client matter ID</span>
            <span>Cost center</span>
            <span>Fee</span>
            <span>Next date</span>
            <span />
          </div>
          {rows.length ? (
            rows.map((r) => (
              <div
                key={`${r.individual_id}-${r.dependent_of ?? ""}`}
                role="row"
                data-testid={`case-row-${r.individual_id}`}
                className={cn(
                  COLS,
                  "h-14 cursor-pointer border-b border-line-2 last:border-b-0 hover:bg-fill-1"
                )}
                tabIndex={0}
                onClick={() => openCase(r)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.target === e.currentTarget)
                    openCase(r);
                }}
              >
                <span
                  className={cn(
                    "flex min-w-0 items-center gap-2",
                    r.dependent_of && "pl-5"
                  )}
                >
                  {r.dependent_of ? (
                    <span
                      aria-hidden
                      className="-mt-2.5 h-3.5 w-2.5 shrink-0 border-b border-l border-line-2"
                    />
                  ) : null}
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-base font-medium">
                      {r.name}
                    </span>
                    <span className="truncate text-sm text-fg-3">
                      {r.dependent_of
                        ? "Dependent"
                        : company.entities.length > 1
                        ? r.entity
                        : r.worksite || ""}
                    </span>
                  </span>
                </span>
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-base">{r.visa ?? "Case"}</span>
                  {!r.open ? <Badge variant="secondary">Closed</Badge> : null}
                </span>
                <span onClick={(e) => e.stopPropagation()} className="min-w-0">
                  {r.linked && company.job_descriptions.length ? (
                    <Select
                      value={r.job_description_id ?? NONE}
                      onValueChange={(v) =>
                        setPart(r, "job_description_id", v === NONE ? null : v)
                      }
                    >
                      <SelectTrigger
                        size="sm"
                        aria-label={`Job for ${r.name}`}
                        className={cn(
                          "h-7 w-full border-transparent px-1.5 text-sm shadow-none hover:border-line-2",
                          !r.job && "text-fg-3"
                        )}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>
                          {(!r.job_description_id && r.job) || "No job yet"}
                        </SelectItem>
                        {company.job_descriptions.map((j) => (
                          <SelectItem key={j.id} value={j.id}>
                            {j.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span
                      className="block truncate text-sm text-fg-3"
                      title={r.job ?? undefined}
                    >
                      {r.job ?? "–"}
                    </span>
                  )}
                </span>
                <span onClick={(e) => e.stopPropagation()}>
                  {r.linked ? (
                    <InlineId
                      value={r.client_matter_id}
                      label={`Client matter ID for ${r.name}`}
                      onSave={saveId(r, "client_matter_id")}
                    />
                  ) : (
                    <span className="text-sm text-fg-3">Through principal</span>
                  )}
                </span>
                <span onClick={(e) => e.stopPropagation()}>
                  {r.linked ? (
                    <InlineId
                      value={r.cost_center}
                      label={`Cost center for ${r.name}`}
                      onSave={saveId(r, "cost_center")}
                    />
                  ) : null}
                </span>
                <FeeCell fee={r.fee} />
                <span
                  className={cn(
                    "truncate text-sm",
                    isSoon(r.next_deadline)
                      ? "font-medium text-warn"
                      : "text-fg-3"
                  )}
                >
                  {r.next_deadline ? formatDeadline(r.next_deadline) : "–"}
                </span>
                <span onClick={(e) => e.stopPropagation()}>
                  {r.linked ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon-xs"
                          variant="ghost"
                          aria-label={`More for ${r.name}`}
                        >
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {company.entities.length > 1
                          ? company.entities
                              .filter((e) => e.id !== r.entity_id)
                              .map((e) => (
                                <DropdownMenuItem
                                  key={e.id}
                                  onSelect={() => setPart(r, "entity_id", e.id)}
                                >
                                  Petition through {e.legal_name}
                                </DropdownMenuItem>
                              ))
                          : null}
                        <DropdownMenuItem
                          onSelect={() =>
                            void companyActions.unlink(
                              company,
                              [r.individual_id],
                              {
                                entity_id: r.entity_id,
                                job_description_id: r.job_description_id,
                                worksite_id: r.worksite_id
                              }
                            )
                          }
                        >
                          <Unlink /> Unlink from {company.name}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </span>
              </div>
            ))
          ) : (
            <p className="px-4 py-8 text-center text-base text-fg-3">
              {cases?.length
                ? "No cases match."
                : `No cases are linked to ${company.name} yet.`}
            </p>
          )}
        </div>
      )}

      <p className="flex items-start gap-2 text-sm text-fg-3">
        <Info className="mt-0.5 size-3.5 shrink-0" />
        The client matter ID is the company&apos;s own number for the case. It
        goes into CLIENT_MATTER_ID on LEDES exports, and e-billing systems
        reject invoices without it.
      </p>

      <LinkCasesDialog
        company={company}
        open={linkOpen}
        onOpenChange={setLinkOpen}
      />
    </div>
  );
};
